import React, { useState, useEffect, useRef } from 'react';
import { BarChart3, Activity, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import Chart from 'chart.js/auto';
import api from '../utils/api';

// Metrics are served by /itops-metrics, which is behind the API Gateway JWT
// authorizer. Always go through the shared api layer so the Okta Bearer token
// is attached (and so proxy-redirect retries are handled) instead of calling
// API Gateway directly.

const TARGETS = { availability: 99.9, uptime: 99.5, sev1_mttr: 120, sev2_mttr: 240, change_success: 95 };
const LOBS = ['Corp', 'DBS', 'Sling', 'Retail Wireless', 'MNO', 'Shared Services'];
const MONTHS_LIST = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MS_KEYS = ['identify','escalate','engage_fa','root_cause','apply_fix','validate'];
const MS_LABELS = ['Time to identify','Duration to escalate','Duration to engage FA','Duration to root cause','Duration to apply fix','Duration to validate fix'];
const MS_COLORS = ['#2196F3','#F44336','#FFC107','#4CAF50','#FF9800','#26C6DA'];

Chart.defaults.color = '#8892b0';
Chart.defaults.borderColor = 'rgba(255,255,255,0.06)';
Chart.defaults.font.size = 11;

const MetricCard = ({ label, value, goal, unit = '%', icon: Icon }) => {
  const met = goal === null ? true : unit === 'min' ? value <= goal : value >= goal;
  return (
    <div className="bg-surface-2 rounded-lg p-4 border border-border">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] text-text-muted uppercase tracking-wide">{label}</span>
        {Icon && <Icon size={14} className="text-text-muted" />}
      </div>
      <div className={`text-2xl font-bold ${met ? 'text-positive' : 'text-negative'}`}>
        {typeof value === 'number' ? (unit === 'min' ? `${Math.round(value)}` : value.toFixed(3)) : value}{unit}
      </div>
      {goal !== null && <div className={`flex items-center gap-1 mt-1 text-[11px] ${met ? 'text-positive' : 'text-negative'}`}>{met ? <CheckCircle size={11} /> : <AlertTriangle size={11} />}<span>Goal: {goal}{unit}</span></div>}
    </div>
  );
};

const ChartBox = ({ title, children, className = '' }) => (
  <div className={`bg-surface rounded-lg border border-border p-4 ${className}`}>
    <h3 className="text-sm font-semibold text-text-primary mb-3">{title}</h3>
    {children}
  </div>
);

function useBarChart(ref, config) {
  const chartRef = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    if (chartRef.current) chartRef.current.destroy();
    chartRef.current = new Chart(ref.current, config);
    return () => { if (chartRef.current) chartRef.current.destroy(); };
  }, [ref, config]);
}

const ITOpsMetrics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLob, setSelectedLob] = useState('Corp');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const fetchData = () => {
    setLoading(true);
    const month = selectedMonth + 1;
    const startDate = `${selectedYear}-${String(month).padStart(2,'0')}-01`;
    const lastDay = new Date(selectedYear, month, 0).getDate();
    const endDate = `${selectedYear}-${String(month).padStart(2,'0')}-${lastDay}`;
    api.request(`/itops-metrics?start_date=${startDate}&end_date=${endDate}`)
      .then(d => { setData(d); setLoading(false); })
      .catch(err => { setError(err.message || 'Failed to load'); setLoading(false); });  };

  useEffect(() => { fetchData(); }, [selectedMonth, selectedYear]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>;
  if (!data || !data.months) return <div className="p-6 text-text-muted">No data available</div>;

  const months = data.months || [];
  const yearAvg = data.year_avg || {};
  const ytdAvg = data.ytd_avg || {};
  const incidents = data.incidents || [];
  const currentMonth = months[months.length - 1] || {};
  const labels = [...months.map(m => m.label), '2025 Avg', 'YTD Avg'];
  const allData = [...months, yearAvg, ytdAvg];

  return (
    <div className="p-6 space-y-6 max-w-[1440px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <BarChart3 size={24} className="text-blue-500" />
          <h1 className="text-xl font-semibold text-text-primary">Service Management Ops Metrics</h1>
        </div>
        <div className="flex items-center gap-2">
          <select value={selectedMonth} onChange={e => setSelectedMonth(+e.target.value)} className="bg-surface-2 border border-border rounded-lg px-3 py-1.5 text-sm text-text-primary">
            {MONTHS_LIST.map((m, i) => <option key={m} value={i}>{m}</option>)}
          </select>
          <select value={selectedYear} onChange={e => setSelectedYear(+e.target.value)} className="bg-surface-2 border border-border rounded-lg px-3 py-1.5 text-sm text-text-primary">
            {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* LOB Tabs */}
      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {LOBS.map(lob => (
          <button key={lob} onClick={() => setSelectedLob(lob)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${selectedLob === lob ? 'border-blue-500 text-blue-400' : 'border-transparent text-text-muted hover:text-text-secondary'}`}>
            {lob === 'Corp' ? 'Corp (All LOBs)' : lob}
          </button>
        ))}
      </div>

      {error && <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-4 py-2 text-sm text-yellow-400">{error}</div>}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <MetricCard label="Incidents (Current)" value={currentMonth.vendor_sev1_count + currentMonth.vendor_sev2_count + currentMonth.internal_sev1_count + currentMonth.internal_sev2_count || 0} goal={null} unit="" icon={Activity} />
        <MetricCard label="Sev 1 MTTR" value={currentMonth.vendor_sev1_mttr || currentMonth.internal_sev1_mttr || 0} goal={TARGETS.sev1_mttr} unit="min" icon={Clock} />
        <MetricCard label="Sev 2 MTTR" value={currentMonth.vendor_sev2_mttr || currentMonth.internal_sev2_mttr || 0} goal={TARGETS.sev2_mttr} unit="min" icon={Clock} />
        <MetricCard label="Availability" value={currentMonth.availability_pct || 100} goal={TARGETS.availability} />
        <MetricCard label="Uptime" value={currentMonth.uptime_pct || 100} goal={TARGETS.uptime} />
        <MetricCard label="Change Success" value={currentMonth.change_success_rate || 0} goal={TARGETS.change_success} icon={CheckCircle} />
      </div>

      {/* Charts Row 1: Incident Count + Uptime */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartBox title={`${selectedLob === 'Corp' ? 'Enterprise' : selectedLob} Major Incident Count`}>
          <StackedBarChart labels={labels} datasets={[
            { label: 'Vendor Sev 1', data: allData.map(m => m.vendor_sev1_count || 0), bg: '#FFB300' },
            { label: 'Vendor Sev 2', data: allData.map(m => m.vendor_sev2_count || 0), bg: '#4CAF50' },
            { label: 'Internal Sev 1', data: allData.map(m => m.internal_sev1_count || 0), bg: '#F44336' },
            { label: 'Internal Sev 2', data: allData.map(m => m.internal_sev2_count || 0), bg: '#2196F3' },
          ]} />
        </ChartBox>
        <ChartBox title={`${selectedLob === 'Corp' ? 'Enterprise' : selectedLob} Critical App Uptime`}>
          <GroupedBarChart labels={labels} datasets={[
            { label: 'Availability (Sev 1 only)', data: allData.map(m => m.availability_pct || 100), bg: '#F44336' },
            { label: 'Uptime (Sev 1 + Sev 2)', data: allData.map(m => m.uptime_pct || 100), bg: '#5C8BCC' },
          ]} yMin={99.2} yMax={100} decimals={3} />
        </ChartBox>
      </div>

      {/* Charts Row 2: MTTR + Change Volume */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartBox title={`${selectedLob === 'Corp' ? 'Enterprise' : selectedLob} MTTR`}>
          <GroupedBarChart labels={labels} datasets={[
            { label: 'Vendor Sev 1', data: allData.map(m => (m.vendor_sev1_mttr || 0) / 60), bg: '#FF9800' },
            { label: 'Vendor Sev 2', data: allData.map(m => (m.vendor_sev2_mttr || 0) / 60), bg: '#689F38' },
            { label: 'Internal Sev 1', data: allData.map(m => (m.internal_sev1_mttr || 0) / 60), bg: '#E53935' },
            { label: 'Internal Sev 2', data: allData.map(m => (m.internal_sev2_mttr || 0) / 60), bg: '#1565C0' },
          ]} yFormat="time" />
        </ChartBox>
        <ChartBox title="Change Volume">
          <GroupedBarChart labels={labels} datasets={[
            { label: 'Software Change Requests', data: allData.map(m => m.scr_count || 0), bg: '#42A5F5' },
            { label: 'Infrastructure Change Requests', data: allData.map(m => m.chg_count || 0), bg: '#EF5350' },
          ]} />
        </ChartBox>
      </div>

      {/* Resolution Milestones — Horizontal stacked bars */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartBox title="Severity 1 — Resolution Milestone Breakdown">
          <MilestoneHorizontal data={allData} labels={labels} sevKey="sev1_milestones" />
        </ChartBox>
        <ChartBox title="Severity 2 — Resolution Milestone Breakdown">
          <MilestoneHorizontal data={allData} labels={labels} sevKey="sev2_milestones" />
        </ChartBox>
      </div>

      {/* Change Reliability — Donut + Big Number */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartBox title="Change Success Rate">
          <DonutChart value={currentMonth.change_success_rate || 0} label="Success Rate" target={95} />
        </ChartBox>
        <ChartBox title="Change/Incident Ratio">
          <div className="flex items-center justify-center h-48">
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-400">{(currentMonth.change_incident_ratio || 0).toFixed(1)}</div>
              <div className="text-sm text-text-muted mt-2">Changes per Incident</div>
              <div className="text-xs text-text-secondary mt-1">YTD Avg: {(ytdAvg.change_incident_ratio || 0).toFixed(1)} | 2025 Avg: {(yearAvg.change_incident_ratio || 0).toFixed(1)}</div>
            </div>
          </div>
        </ChartBox>
        <ChartBox title="Change Volume (Current)">
          <div className="flex items-center justify-center h-48 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-400">{currentMonth.scr_count || 0}</div>
              <div className="text-xs text-text-muted mt-1">SCR (Software)</div>
            </div>
            <div className="w-px h-16 bg-border" />
            <div className="text-center">
              <div className="text-3xl font-bold text-red-400">{currentMonth.chg_count || 0}</div>
              <div className="text-xs text-text-muted mt-1">CHG (Infra)</div>
            </div>
          </div>
        </ChartBox>
      </div>

      {/* Incident Table */}
      {incidents.length > 0 && (
        <ChartBox title={`${currentMonth.label || ''} Major Incidents (Sev 1 and 2)`}>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-surface-2">
                <tr>{['Number','Summary','Date','Sev','LOB','Duration','Source','Change?','Critical Apps'].map(h => <th key={h} className="px-3 py-2 text-left text-text-muted font-medium whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-border">
                {incidents.map((inc, i) => (
                  <tr key={i} className={`hover:bg-surface-2 ${inc.severity === 1 ? 'border-l-2 border-l-red-500' : 'border-l-2 border-l-amber-500'}`}>
                    <td className="px-3 py-2 font-mono text-blue-400"><a href={`https://dish.service-now.com/nav_to.do?uri=incident.do?sysparm_query=number=${inc.number}`} target="_blank" rel="noopener noreferrer" className="hover:underline">{inc.number}</a></td>
                    <td className="px-3 py-2 text-text-primary max-w-[220px] truncate">{inc.summary}</td>
                    <td className="px-3 py-2 text-text-muted whitespace-nowrap">{inc.date}</td>
                    <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${inc.severity === 1 ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>Sev {inc.severity}</span></td>
                    <td className="px-3 py-2 text-text-secondary">{inc.lob}</td>
                    <td className="px-3 py-2">{inc.duration_min}m</td>
                    <td className="px-3 py-2">{inc.vendor_internal}</td>
                    <td className="px-3 py-2">{inc.change_related ? 'Yes' : 'No'}</td>
                    <td className="px-3 py-2 text-text-muted max-w-[160px] truncate">{(inc.critical_apps || []).join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartBox>
      )}
    </div>
  );
};

// === Reusable Chart Components ===

function StackedBarChart({ labels, datasets }) {
  const ref = useRef(null); const c = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    if (c.current) c.current.destroy();
    c.current = new Chart(ref.current, { type: 'bar', data: { labels, datasets: datasets.map(d => ({ label: d.label, data: d.data, backgroundColor: d.bg })) }, options: { responsive: true, plugins: { legend: { position: 'top', labels: { boxWidth: 12, padding: 8 } } }, scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true } } } });
    return () => c.current?.destroy();
  }, [labels, datasets]);
  return <canvas ref={ref} height="180" />;
}

function GroupedBarChart({ labels, datasets, yMin, yMax, yFormat, decimals }) {
  const ref = useRef(null); const c = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    if (c.current) c.current.destroy();
    const yOpts = { beginAtZero: !yMin };
    if (yMin !== undefined) yOpts.min = yMin;
    if (yMax !== undefined) yOpts.max = yMax;
    if (decimals) yOpts.ticks = { callback: v => v.toFixed(decimals) };
    if (yFormat === 'time') yOpts.ticks = { callback: v => { const h = Math.floor(v); const m = Math.round((v-h)*60); return `${h}:${String(m).padStart(2,'0')}`; } };
    c.current = new Chart(ref.current, { type: 'bar', data: { labels, datasets: datasets.map(d => ({ label: d.label, data: d.data, backgroundColor: d.bg })) }, options: { responsive: true, plugins: { legend: { position: 'top', labels: { boxWidth: 12, padding: 8 } } }, scales: { y: yOpts } } });
    return () => c.current?.destroy();
  }, [labels, datasets, yMin, yMax, yFormat, decimals]);
  return <canvas ref={ref} height="180" />;
}

function MilestoneHorizontal({ data, labels, sevKey }) {
  const ref = useRef(null); const c = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    if (c.current) c.current.destroy();
    c.current = new Chart(ref.current, { type: 'bar', data: { labels, datasets: MS_KEYS.map((k, i) => ({
      label: MS_LABELS[i], data: data.map(m => (m[sevKey] || {})[k] || 0), backgroundColor: MS_COLORS[i]
    })) }, options: { indexAxis: 'y', responsive: true, plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, padding: 6, font: { size: 10 } } } }, scales: { x: { stacked: true, title: { display: true, text: 'Minutes' } }, y: { stacked: true } } } });
    return () => c.current?.destroy();
  }, [data, labels, sevKey]);
  return <canvas ref={ref} height="220" />;
}

function DonutChart({ value, label, target }) {
  const ref = useRef(null); const c = useRef(null);
  const met = value >= target;
  useEffect(() => {
    if (!ref.current) return;
    if (c.current) c.current.destroy();
    c.current = new Chart(ref.current, { type: 'doughnut', data: { labels: ['Success', 'Failed'], datasets: [{ data: [value, 100 - value], backgroundColor: [met ? '#22c55e' : '#ef4444', 'rgba(255,255,255,0.05)'], borderWidth: 0 }] }, options: { responsive: true, cutout: '70%', plugins: { legend: { display: false } } } });
    return () => c.current?.destroy();
  }, [value, met]);
  return (
    <div className="relative flex items-center justify-center h-48">
      <canvas ref={ref} />
      <div className="absolute text-center">
        <div className={`text-2xl font-bold ${met ? 'text-positive' : 'text-negative'}`}>{value.toFixed(1)}%</div>
        <div className="text-[10px] text-text-muted">Goal: {target}%</div>
      </div>
    </div>
  );
}

export default ITOpsMetrics;
